import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { useEnvironmentFetch } from '../use-environment-fetch/use-environment-fetch';
import { useConfigAppParams } from '../use-config-app-params/use-config-app-params';
import { useAuthoringEnvironments } from '../use-authoring-environments/use-authoring-environments';

type UploadFileType = 'image' | 'pdf';

/**
 * Upload a file to the authoring environment's config-file-upload endpoint and
 * return the fileUploadId. Uses the current authoring environment from the
 * entity details data store.
 */
export const useConfigFileUpload = () => {
  const { environmentKey } = useConfigAppParams();
  const { currentAuthoringEnvironment } = useAuthoringEnvironments({
    environmentKey,
  });
  const environmentFetch = useEnvironmentFetch(
    currentAuthoringEnvironment as any,
  );

  const mutation = useMutation({
    mutationFn: async ({
      file,
      fileType,
    }: {
      file: File;
      fileType: UploadFileType;
    }) => {
      const formData = new FormData();
      const payload = {
        type: 'config-file-upload',
        attributes: { fileType },
      };
      formData.append('data', JSON.stringify(payload));
      formData.append('file', file);

      const response = await environmentFetch(
        '/v1/config-file-upload',
        {
          method: 'POST',
          body: formData,
        },
        undefined,
      );

      if (!response.ok) {
        throw new Error('UPLOAD_FILE_ERROR');
      }

      const json = await response.json();
      const fileUploadId: string | undefined =
        json?.data?.attributes?.fileUploadId;
      if (!fileUploadId) {
        throw new Error('UPLOAD_FILE_ERROR');
      }
      return fileUploadId;
    },
  });

  const upload = React.useCallback(
    async (file: File, fileType: UploadFileType = 'image') =>
      mutation.mutateAsync({ file, fileType }),
    [mutation],
  );

  return {
    upload,
    isLoading: mutation.status === 'pending',
    isError: mutation.status === 'error',
    error: (mutation.error as Error) ?? null,
    reset: mutation.reset,
  };
};
